import React, { useState, useMemo, useEffect } from "react";
import { Icon, Tabs, TabsList, TabsPanel, TabsTrigger } from "../..";
import Modal from "../../UIElements/Modal";
import WidgetTypeCard from "./WidgetTypeCard";
import WidgetConfigurator from "./WidgetConfigurator";
import WidgetCodeSnippet from "./WidgetCodeSnippet";
import WidgetPreview from "./WidgetPreview";
import { getFinitlessChatSiteUrl } from "../../../../lib/linkInBio";
import "./ai-chat-widget-ordering.css";

const WIDGET_TYPES = [
  {
    id: "floating",
    name: "Floating Chat",
    description:
      "WhatsApp-style floating button that stays visible in the corner of the screen.",
    icon: "MessageCircle",
    popular: true,
    features: ["Always visible", "Smooth animations", "Fullscreen on mobile"],
    enabled: true,
  },
  {
    id: "popup",
    name: "Popup Modal",
    description: "Opens in a centered modal window when clicking a button.",
    icon: "Maximize2",
    popular: false,
    features: ["Centered modal", "Elegant overlay"],
    enabled: true,
  },
  {
    id: "inline",
    name: "Inline (Embedded)",
    description: "Integrates directly into the page content.",
    icon: "Layout",
    popular: false,
    features: ["No popups", "Integrated in flow", "Customizable height"],
    enabled: false,
  },
];

const getAgentChatUrl = (agent) => getFinitlessChatSiteUrl(agent?.site) ?? "";

const WidgetConfigModal = ({ isOpen, onClose, agent }) => {
  const [selectedType, setSelectedType] = useState("floating");
  const [config, setConfig] = useState({
    chatUrl: "",
    primaryColor: "#00BFFF",
    secondaryColor: "#FF00FF",
    position: "bottom-right",
    buttonSize: 50,
    chatWidth: 400,
    chatHeight: 650,
    showHeader: true,
    iconUrl: "",
  });
  const [activeTab, setActiveTab] = useState("configure");

  useEffect(() => {
    if (agent) {
      const chatUrl = getAgentChatUrl(agent);
      setConfig((prev) => ({ ...prev, chatUrl }));
    }
  }, [agent]);

  const handleConfigChange = (key, value) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  };

  const selectedWidget = useMemo(
    () => WIDGET_TYPES.find((w) => w.id === selectedType),
    [selectedType],
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Widget Configuration"
      subtitle="Generate embeddable code for this AI agent"
      maxWidth="max-w-6xl"
    >
      <div className="ordering-widget-surface space-y-6">
        {/* Integration Instructions */}
        <div className="bg-card border-border rounded-xl border p-6">
          <h3 className="text-card-foreground mb-4 flex items-center gap-2 text-lg font-semibold">
            <Icon name="HelpCircle" className="text-primary h-5 w-5" />
            Steps to configure your widget
          </h3>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <div className="flex gap-4">
              <div className="bg-primary/10 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full">
                <span className="text-primary font-bold">1</span>
              </div>
              <div>
                <h4 className="text-card-foreground mb-1 font-medium">
                  Configure your widget
                </h4>
                <p className="text-muted-foreground text-sm">
                  Customize colors, position, and behavior to match your brand.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="bg-primary/10 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full">
                <span className="text-primary font-bold">2</span>
              </div>
              <div>
                <h4 className="text-card-foreground mb-1 font-medium">
                  Copy the code
                </h4>
                <p className="text-muted-foreground text-sm">
                  Go to the &quot;Code&quot; tab and copy the generated snippet.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="bg-primary/10 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full">
                <span className="text-primary font-bold">3</span>
              </div>
              <div>
                <h4 className="text-card-foreground mb-1 font-medium">
                  Paste in your site
                </h4>
                <p className="text-muted-foreground text-sm">
                  Insert the code before the &lt;/body&gt;&nbsp;tag in your
                  HTML.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Widget Type Selection */}
        <div className="bg-card border-border rounded-xl border p-6">
          <h3 className="text-card-foreground mb-4 flex items-center gap-2 text-lg font-semibold">
            <Icon name="Layers" className="text-primary h-5 w-5" />
            Widget Type
          </h3>
          <div className="flex flex-col gap-4 md:flex-row">
            {WIDGET_TYPES.filter((widget) => widget.enabled).map((widget) => (
              <div key={widget.id} className="flex-1">
                <WidgetTypeCard
                  widget={widget}
                  isSelected={selectedType === widget.id}
                  onSelect={() => setSelectedType(widget.id)}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="bg-card border-border overflow-hidden rounded-lg border"
        >
          <TabsList className="grid grid-cols-1 gap-0 sm:grid-cols-3">
            <TabsTrigger value="configure" className="gap-2">
              <Icon name="Settings" className="h-4 w-4" />
              1. Configure
            </TabsTrigger>
            <TabsTrigger value="code" className="gap-2">
              <Icon name="Code" className="h-4 w-4" />
              2. Code
            </TabsTrigger>
            <TabsTrigger value="preview" className="gap-2">
              <Icon name="Eye" className="h-4 w-4" />
              Preview
            </TabsTrigger>
          </TabsList>

          <TabsPanel value="configure" className="p-6">
            <WidgetConfigurator
              config={config}
              widgetType={selectedType}
              onChange={handleConfigChange}
            />
          </TabsPanel>
          <TabsPanel value="code" className="p-6">
            <WidgetCodeSnippet
              config={config}
              widgetType={selectedType}
              widgetName={selectedWidget?.name}
            />
          </TabsPanel>
          <TabsPanel value="preview" className="p-6">
            <WidgetPreview config={config} widgetType={selectedType} />
          </TabsPanel>
        </Tabs>
      </div>
    </Modal>
  );
};

export default WidgetConfigModal;
